import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon, ArrowRight } from '@phosphor-icons/react';
import '../styles/App.css';

/**
 * Hand Icon Component (shared with other dashboard pages)
 */
const HandIcon = ({ size = 40 }) => (
  <div
    role="img"
    aria-label="Handy Co Logo"
    style={{
      width: size,
      height: size,
      backgroundColor: 'var(--crm-text, #9CACA6)',
      WebkitMaskImage: 'url(/assets/icons/icon_handyco.svg)',
      WebkitMaskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      WebkitMaskSize: 'contain',
      maskImage: 'url(/assets/icons/icon_handyco.svg)',
      maskRepeat: 'no-repeat',
      maskPosition: 'center',
      maskSize: 'contain',
      flexShrink: 0,
    }}
  />
);

/**
 * Design system entries — each points to the public showcase page
 */
const designSystems = [
  {
    id: 'handyco',
    name: 'Handy Co',
    description: 'Forest-toned palette with Book Antiqua headlines and Lato body text. Dark-first surfaces built for a modern development house.',
    route: '/ds/handyco',
    colors: ['#19231F', '#9CACA6', '#5A6E65', '#3B4E45', '#E88888'],
  },
  {
    id: 'iiq',
    name: 'InterSession IQ',
    description: 'Warm, earthy palette with DM Serif Display headings and Inter body text. Sage and clay accents for a calm, grounded health-care experience.',
    route: '/ds/iiq',
    colors: ['#F5F2EE', '#3D8B37', '#C06030', '#1F1B18', '#D03E3E'],
  },
  {
    id: 'localite',
    name: 'Localite',
    description: 'Bold civic-tech design system with a purple-forward palette. Space Grotesk headings, color-blocking, and hard-edged shadows for accessible government interfaces.',
    route: '/ds/localite',
    colors: ['#1C1917', '#7B3FD4', '#D69E2E', '#2A9D8F', '#C5393A'],
  },
];

/**
 * Design Systems listing page — auth-locked, shows all available design systems
 */
const DesignSystems = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const [theme, setTheme] = useState(() => localStorage.getItem('crm_theme') || 'dark');

  useEffect(() => {
    const checkSession = async () => {
      const storedToken = localStorage.getItem('crm_session');
      const storedUser = localStorage.getItem('crm_user');

      if (!storedToken) {
        setAuthLoading(false);
        navigate('/dashboard');
        return;
      }

      try {
        const res = await fetch('/api/crm/auth/session', {
          headers: { 'Authorization': `Bearer ${storedToken}` }
        });
        const data = await res.json();

        if (res.ok && data.valid) {
          setUser(storedUser ? JSON.parse(storedUser) : data.user);
          setAuthenticated(true);
        } else {
          localStorage.removeItem('crm_session');
          localStorage.removeItem('crm_user');
          navigate('/dashboard');
        }
      } catch (err) {
        console.error('Session check error:', err);
        navigate('/dashboard');
      } finally {
        setAuthLoading(false);
      }
    };

    checkSession();
  }, [navigate]);

  const handleSignOut = () => {
    localStorage.removeItem('crm_session');
    localStorage.removeItem('crm_user');
    navigate('/dashboard');
  };

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('crm_theme', next);
  };

  if (authLoading) {
    return (
      <div className={`crm-page ${theme === 'light' ? 'crm-light' : ''}`}>
        <div className="crm-login-wrapper">
          <div className="crm-login">
            <HandIcon size={80} />
            <div className="crm-auth-loading">
              <div className="crm-spinner" />
              <p>Loading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!authenticated) return null;

  return (
    <div className={`crm-page ${theme === 'light' ? 'crm-light' : ''}`}>
      <header className="crm-header">
        <div className="crm-header-brand" onClick={() => navigate('/dashboard')}>
          <HandIcon size={36} />
          <span className="crm-header-title">
            <span className="crm-header-wordmark">HANDY CO</span>
          </span>
        </div>
        <nav className="crm-header-nav">
          <button className="crm-nav-link" onClick={() => navigate('/dashboard')}>Overview</button>
          <button className="crm-nav-link" onClick={() => navigate('/dashboard/all')}>Clients</button>
          <button className="crm-nav-link" onClick={() => navigate('/dashboard/tasks')}>Tasks</button>
          <button className="crm-nav-link" onClick={() => navigate('/dashboard/thoughts')}>Thoughts</button>
          <button className="crm-nav-link active" onClick={() => navigate('/ds')}>Design Systems</button>
        </nav>
        <div className="crm-header-actions">
          <button className="crm-button ghost crm-theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
            {theme === 'dark' ? <Sun weight="fill" size={18} /> : <Moon weight="fill" size={18} />}
          </button>
          {user && (
            <button className="crm-button ghost crm-user-btn" onClick={handleSignOut}>
              {user.picture && (
                <img src={user.picture} alt="" className="crm-user-avatar" />
              )}
              <span className="crm-user-name">{user.name?.split(' ')[0] || 'Sign out'}</span>
            </button>
          )}
        </div>
      </header>

      <main className="crm-main crm-main-full">
        <div className="crm-list-header">
          <div>
            <h2 className="crm-section-title">Design Systems</h2>
            <p className="crm-section-subtitle">{designSystems.length} systems</p>
          </div>
        </div>

        <div className="crm-ds-grid">
          {designSystems.map((ds) => (
            <button
              key={ds.id}
              className="crm-ds-card"
              onClick={() => navigate(ds.route)}
            >
              <div className="crm-ds-card-colors">
                {ds.colors.map((color, i) => (
                  <div
                    key={i}
                    className="crm-ds-swatch"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <div className="crm-ds-card-body">
                <h3 className="crm-ds-card-title">{ds.name}</h3>
                <p className="crm-ds-card-desc">{ds.description}</p>
              </div>
              <span className="crm-ds-card-arrow"><ArrowRight weight="bold" size={18} /></span>
            </button>
          ))}
        </div>
      </main>
    </div>
  );
};

export default DesignSystems;
